import React, { useState } from 'react';
import { onboardClient } from '../../lib/api';

interface Props {
  showToast: (msg: string, type?: 'success' | 'error') => void;
  onDone: () => void;
}

type Step = 1 | 2 | 3;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function AdminOnboardWizard({ showToast, onDone }: Props) {
  const [step, setStep] = useState<Step>(1);

  const [company, setCompany] = useState('');
  const [maxUsers, setMaxUsers] = useState<number>(5);
  const [trialDays, setTrialDays] = useState(14);
  const [adminName,  setAdminName]  = useState('');
  const [adminEmail, setAdminEmail] = useState('');

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ trialEndsAt: string | null; maxUsers: number } | null>(null);

  const canNext = () => {
    if (step === 1) return !!company.trim();
    if (step === 2) return Number.isInteger(maxUsers) && maxUsers >= 1 && maxUsers <= 1000 && trialDays >= 0 && trialDays <= 365;
    if (step === 3) return !!adminEmail.trim();
    return false;
  };

  const handleSubmit = async () => {
    setBusy(true);
    const res = await onboardClient({
      company_name: company.trim(),
      admin_email: adminEmail.trim(),
      admin_name: adminName.trim() || undefined,
      max_users: maxUsers,
      trial_days: trialDays,
    });
    setBusy(false);
    if (res.ok && res.data) {
      setResult({ trialEndsAt: res.data.trial_ends_at, maxUsers });
      showToast(`Created org "${company}"`, 'success');
    } else {
      showToast(res.message || res.error || 'Onboarding failed', 'error');
    }
  };

  if (result) {
    return (
      <div className="pane-enter">
        <div className="card" style={{ maxWidth: 560, margin: '0 auto' }}>
          <div className="text-xl font-bold mb-2">Organisation created</div>
          <div className="text-sm text-muted mb-5">
            <strong>{company}</strong> is ready with <strong>{result.maxUsers} seat{result.maxUsers !== 1 ? 's' : ''}</strong>.
            An invite email has been sent to <strong>{adminEmail}</strong>.
          </div>
          {result.trialEndsAt ? (
            <div className="text-sm mb-4" style={{ padding: 'var(--sp-3)', background: 'var(--amber-bg)', color: 'var(--amber)', borderRadius: 'var(--r)' }}>
              Trial expires: {formatDate(result.trialEndsAt)}
            </div>
          ) : (
            <div className="text-sm mb-4" style={{ padding: 'var(--sp-3)', background: 'var(--teal-bg)', color: 'var(--teal)', borderRadius: 'var(--r)' }}>
              Active subscription (no trial period)
            </div>
          )}
          <div className="flex gap-2">
            <button className="btn btn-primary" onClick={onDone}>Back to organisations</button>
            <button
              className="btn btn-ghost"
              onClick={() => {
                setResult(null); setStep(1);
                setCompany(''); setAdminName(''); setAdminEmail('');
                setMaxUsers(5); setTrialDays(14);
              }}
            >Onboard another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pane-enter">
      <div className="card" style={{ maxWidth: 560, margin: '0 auto' }}>
        <div className="text-xl font-bold">Onboard a new organisation</div>
        <div className="text-sm text-muted mt-1 mb-5">Three steps. Admin will receive an invite email.</div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map(n => (
            <React.Fragment key={n}>
              <div
                style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: step >= (n as Step) ? 'var(--accent)' : 'var(--surface2)',
                  color: step >= (n as Step) ? '#fff' : 'var(--text3)',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12,
                  transition: 'background var(--dur-fast) var(--ease)',
                }}
              >{n}</div>
              {n < 3 && (
                <div
                  style={{
                    flex: 1, height: 2,
                    background: step > (n as Step) ? 'var(--accent)' : 'var(--border)',
                    transition: 'background var(--dur-fast) var(--ease)',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div className="flex flex-col gap-3">
            <div>
              <label className="field-label">Organisation name *</label>
              <input
                className="input"
                autoFocus
                value={company}
                onChange={e => setCompany(e.target.value)}
                placeholder="Acme Logistics"
              />
              <div className="field-hint">Shown to users in the app header.</div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-col gap-4">
            <div>
              <label className="field-label">Seats *</label>
              <input
                className="input"
                type="number"
                min={1}
                max={1000}
                value={maxUsers}
                onChange={e => setMaxUsers(Math.max(1, parseInt(e.target.value) || 1))}
                autoFocus
              />
              <div className="field-hint">How many users this customer is paying for. Can be changed later from the org detail page.</div>
            </div>
            <div>
              <label className="field-label">Trial length (days)</label>
              <input
                className="input"
                type="number"
                value={trialDays}
                min={0}
                max={365}
                onChange={e => setTrialDays(Math.max(0, parseInt(e.target.value) || 0))}
              />
              <div className="field-hint">Set to 0 for immediate active subscription (no trial). Default 14.</div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col gap-3">
            <div>
              <label className="field-label">Admin full name</label>
              <input
                className="input"
                value={adminName}
                onChange={e => setAdminName(e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label className="field-label">Admin email *</label>
              <input
                className="input"
                type="email"
                value={adminEmail}
                onChange={e => setAdminEmail(e.target.value)}
                placeholder="admin@acme.co.nz"
                autoFocus
              />
              <div className="field-hint">This person becomes the Account Owner. Invite email goes to this address.</div>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-6">
          <button
            className="btn btn-ghost"
            onClick={() => setStep((step - 1) as Step)}
            disabled={step === 1 || busy}
            style={{ visibility: step === 1 ? 'hidden' : 'visible' }}
          >‹ Back</button>
          {step < 3 ? (
            <button
              className="btn btn-primary"
              onClick={() => setStep((step + 1) as Step)}
              disabled={!canNext()}
            >Next ›</button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!canNext() || busy}
            >{busy ? 'Creating…' : 'Create organisation'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
